import { useCallback, useEffect, useState } from "preact/hooks";
import PropTypes from "prop-types";
import Button from "@/components/buttons/button.jsx";
import useTiltifyConfig from "@/hooks/useTiltifyConfig.js";
import { isTestMode } from "@/util/const.js";
import { isTiltifyTokenExpired, refreshTiltifyToken } from "@/util/tiltifyHelper.js";
import { TiltifyOAuthButton } from "./tiltify-oauth-button.jsx";

TiltifySetup.propTypes = {
  onSignIn: PropTypes.func,
  onSignOut: PropTypes.func,
};

export default function TiltifySetup({ onSignIn = (_) => {}, onSignOut = () => {} }) {
  const config = useTiltifyConfig();
  const tiltifyUsername = config.tiltifyUsername || localStorage.getItem("tiltifyUsername");
  const tiltifyUserSlug = config.tiltifyUserSlug || localStorage.getItem("tiltifyUserSlug");

  const [isTiltifySetup, setTiltifySetup] = useState(
    tiltifyUsername !== null &&
      tiltifyUsername.length > 0 &&
      tiltifyUserSlug !== null &&
      tiltifyUserSlug.length > 0,
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Clears all Tiltify user, campaign, and token data from the reactive config
   * and localStorage, then resets the component to the logged-out state.
   */
  const logoutTiltify = useCallback(() => {
    config.tiltifyUserSlug = "";
    config.tiltifyUsername = "";
    config.tiltifyUserID = "";
    config.tiltifyCampaignID = "";
    config.tiltifyPollID = "";
    config.tiltifyCampaignSlug = "";
    config.tiltifyCampaignGoal = "";
    config.tiltifyCampaignName = "";
    config.tiltifyAccessToken = "";
    config.tiltifyRefreshToken = "";
    config.tiltifyTokenExpiresIn = "";
    config.tiltifyTokenCreatedAt = "";

    localStorage.removeItem("tiltifyUserSlug");
    localStorage.removeItem("tiltifyUsername");
    localStorage.removeItem("tiltifyUserID");
    localStorage.removeItem("tiltifyCampaignSlug");
    localStorage.removeItem("tiltifyCampaignID");
    localStorage.removeItem("tiltifyPollID");
    localStorage.removeItem("tiltifyCampaignGoal");
    localStorage.removeItem("tiltifyCampaignName");
    localStorage.removeItem("tiltifyAccessToken");
    localStorage.removeItem("tiltifyRefreshToken");
    localStorage.removeItem("tiltifyTokenExpiresIn");
    localStorage.removeItem("tiltifyTokenCreatedAt");

    setTiltifySetup(false);
    onSignOut();
  }, [config, onSignOut]);

  /**
   * Persists a refreshed token set into the reactive config and localStorage.
   *
   * @param {{ accessToken: string, refreshToken: string, expiresIn: string|number, createdAt: string }} tokens
   */
  const saveTokens = useCallback(
    (tokens) => {
      config.tiltifyAccessToken = tokens?.accessToken ?? "";
      config.tiltifyRefreshToken = tokens?.refreshToken ?? "";
      config.tiltifyTokenExpiresIn = tokens?.expiresIn ?? "";
      config.tiltifyTokenCreatedAt = tokens?.createdAt ?? "";
      localStorage.setItem("tiltifyAccessToken", tokens?.accessToken ?? "");
      localStorage.setItem("tiltifyRefreshToken", tokens?.refreshToken ?? "");
      localStorage.setItem("tiltifyTokenExpiresIn", tokens?.expiresIn ?? "");
      localStorage.setItem("tiltifyTokenCreatedAt", tokens?.createdAt ?? "");
    },
    [config],
  );

  /**
   * On mount (and whenever the setup state changes), validate the stored Tiltify
   * tokens:
   *
   *  1. If no refresh token is present the session cannot be recovered → sign out.
   *  2. If the access token is absent or within 5 minutes of expiry, attempt a
   *     silent refresh using the stored refresh token.
   *  3. If the refresh call fails or returns nothing → sign out.
   */
  useEffect(() => {
    if (!isTiltifySetup || isTestMode) {
      return;
    }

    const storedRefreshToken =
      (config.tiltifyRefreshToken || localStorage.getItem("tiltifyRefreshToken")) ?? "";
    const storedAccessToken =
      (config.tiltifyAccessToken || localStorage.getItem("tiltifyAccessToken")) ?? "";

    // No refresh token → no way to recover the session.
    if (!storedRefreshToken) {
      logoutTiltify();
      return;
    }

    const createdAt =
      (config.tiltifyTokenCreatedAt || localStorage.getItem("tiltifyTokenCreatedAt")) ?? "";
    const expiresIn =
      (config.tiltifyTokenExpiresIn || localStorage.getItem("tiltifyTokenExpiresIn")) ?? "";

    // If the access token is missing or expiring soon, refresh silently.
    if (!storedAccessToken || isTiltifyTokenExpired(createdAt, expiresIn)) {
      setIsRefreshing(true);
      refreshTiltifyToken(storedRefreshToken)
        .then((user) => {
          if (!user) {
            logoutTiltify();
            return;
          }
          saveTokens(user.tokens);
        })
        .catch(() => logoutTiltify())
        .finally(() => setIsRefreshing(false));
    }
  }, [isTiltifySetup, config, logoutTiltify, saveTokens]);

  if (!isTiltifySetup) {
    return (
      <TiltifyOAuthButton
        setTiltifyUser={(user) => {
          config.tiltifyUserSlug = user.slug;
          config.tiltifyUserID = user.id;
          config.tiltifyUsername = user.username;
          localStorage.setItem("tiltifyUserID", user.id);
          localStorage.setItem("tiltifyUsername", user.username);
          localStorage.setItem("tiltifyUserSlug", user.slug);

          saveTokens(user.tokens);

          setTiltifySetup(
            config.tiltifyUsername.length > 0 &&
              config.tiltifyUserSlug.length > 0 &&
              config.tiltifyUserID.length > 0,
          );

          onSignIn({
            userID: user.id,
            username: user.username,
            slug: user.slug,
          });
        }}
      />
    );
  }

  if (isRefreshing) {
    return (
      <div className="flex items-center gap-2 min-h-10">
        <span className="loading loading-spinner loading-sm" />
        <span className="text-sm">Reconnecting to Tiltify…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-10">
      <div className="flex items-center justify-between">
        <article className="prose text-sm">
          Your Tiltify account, <b>{isTestMode ? "Test User" : config.tiltifyUsername}</b>, has been
          connected
        </article>

        <Button
          style="accent"
          mode="button"
          disabled={isTestMode}
          onClick={logoutTiltify}
          size="small"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
